import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  Card,
  Divider,
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import supabase from '../../lib/supabaesClient';

export const redirectURL = process.env.REACT_APP_AUTH_REDIRECT_URL;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  const redirectTo = location.state?.from || '/';

  useEffect(() => {
    const checkSession = async () => {
      const session = await supabase.auth.getSession();
      if (session.data?.session) {
        navigate(redirectTo);
      }
      setInitialLoading(false);
    };

    checkSession();
  }, [navigate, redirectTo]);

  const handleGitHubLogin = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: redirectURL
      }
    });

    if (error) {
      console.error('GitHub login error:', error.message);
    }
    setLoading(false);
  };

  const handleGoHome = () => {
    navigate('/');
  };

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 3,
      }}
    >
      <Card
        elevation={0}
        sx={{
          maxWidth: '400px',
          width: '100%',
          p: 4,
          borderRadius: 2,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 3,
          }}
        >
          로그인
        </Typography>

        {loading ? (
          <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <>
            <Button
              variant="contained"
              fullWidth
              onClick={handleGitHubLogin}
              startIcon={<GitHubIcon />}
              sx={{
                py: 1.5,
                bgcolor: 'grey.900',
                '&:hover': {
                  bgcolor: 'grey.800',
                },
              }}
            >
              GitHub로 로그인
            </Button>

            <Divider sx={{ my: 4 }}>또는</Divider>

            <Button
              variant="outlined"
              fullWidth
              onClick={handleGoHome}
              startIcon={<HomeIcon />}
              sx={{
                py: 1.5,
                borderColor: 'grey.300',
                color: 'grey.700',
                '&:hover': {
                  bgcolor: 'grey.50',
                  borderColor: 'grey.400',
                },
              }}
            >
              홈으로 돌아가기
            </Button>
          </>
        )}

        <Typography
          variant="body2"
          sx={{
            mt: 4,
            color: 'grey.600',
            fontSize: '0.875rem',
          }}
        >
          GitHub 계정으로 로그인하여 서비스를 이용하세요.
        </Typography>
      </Card>
    </Box>
  );
};

export default LoginPage;